// Phase 22 — Signup options CTA block. Surfaces the four key entry paths
// (Affiliate, Driver, Partner, Franchisee) on Home / Supplies / Ecosystem
// public pages. Each link preserves any `?ref=<slug>` query so referrer
// attribution survives the click — see lib/affiliateTracking for the
// global capture/persist logic.

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Rocket, Truck, Store, Building2, ChevronRight } from "lucide-react";

const OPTIONS = [
  {
    role: "affiliate",
    label: "Become an Affiliate",
    blurb: "Share Lifted with your audience and earn lifetime commissions.",
    icon: Rocket,
    to: "/affiliate-zone",
    color: "from-lifted-green/20 to-emerald-500/10 border-lifted-green/30 text-lifted-green",
    testid: "signup-cta-affiliate",
  },
  {
    role: "driver",
    label: "Drive for Lifted",
    blurb: "Same-day delivery driver. Flexible shifts. Real earnings.",
    icon: Truck,
    to: "/driver-signup",
    color: "from-sky-500/20 to-blue-500/10 border-sky-500/30 text-sky-300",
    testid: "signup-cta-driver",
  },
  {
    role: "partner",
    label: "Become a Partner",
    blurb: "Plug your store into the Lifted network. Sell to our community.",
    icon: Store,
    to: "/partner-signup",
    color: "from-purple-500/20 to-fuchsia-500/10 border-purple-500/30 text-purple-300",
    testid: "signup-cta-partner",
  },
  {
    role: "franchisee",
    label: "Open a Franchise",
    blurb: "Operate a Lifted territory. Local impact, ecosystem reach.",
    icon: Building2,
    to: "/franchise/signup",
    color: "from-amber-500/20 to-orange-500/10 border-amber-500/30 text-amber-300",
    testid: "signup-cta-franchisee",
  },
];

/**
 * Append any `?ref=<slug>` already on the current URL to outbound signup
 * links so attribution survives a navigation. The affiliate tracking
 * library also reads cookies, but query-string is the most explicit hop.
 */
function withRef(to, search) {
  const sp = new URLSearchParams(search || "");
  const ref = sp.get("ref");
  if (!ref) return to;
  const sep = to.includes("?") ? "&" : "?";
  return `${to}${sep}ref=${encodeURIComponent(ref)}`;
}

export default function SignupOptionsSection({
  title = "Join the Lifted Ecosystem",
  subtitle = "Four paths. One ecosystem. Choose the role that fits — you can always add more later.",
  testid = "signup-options",
}) {
  const loc = useLocation();
  return (
    <section className="py-14 lg:py-20" data-testid={testid}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="font-anton uppercase text-3xl sm:text-4xl lg:text-5xl text-white">
            {title}
          </h2>
          <p className="text-white/65 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <Link
                key={o.role}
                to={withRef(o.to, loc.search)}
                data-testid={o.testid}
                className={`group rounded-xl border bg-gradient-to-br ${o.color} p-5 transition-transform hover:-translate-y-0.5`}
              >
                <div className="h-10 w-10 rounded-lg border border-current/40 flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-anton uppercase text-lg text-white">{o.label}</h3>
                <p className="text-sm text-white/70 mt-2 leading-snug">{o.blurb}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bebas tracking-[0.18em] uppercase">
                  Get started <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
